import { useMemo } from 'react'
import type { MatchDetailResponse } from '../../../../../api/useSupplierReconciliation/useMatchDetail'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { formatInvoiceDate } from '../columnDefs/detailColumnDefs/utils/formatters'

dayjs.extend(utc)
dayjs.extend(timezone)

// Interface for voided invoice details - kept for compatibility with API types
interface VoidedInvoiceDetail {
  type: 'supply' | 'tax'
  product?: {
    internal_name?: string
    source_name?: string
  }
  total?: number
  gross_volume?: number
  net_volume?: number
  rate?: number
  store_number?: string
  tax_total?: number
  product_total?: number
  tax_type?: string
  tax_authority?: string
  state?: string
}

// Interface for voided invoices - kept for compatibility with API types
export interface VoidedInvoice {
  _id: string
  invoice_number: string
  invoice_date: string
  terminal?: {
    internal_name?: string
    source_name?: string
  }
  supplier?: {
    internal_name?: string
    source_name?: string
  }
  details?: VoidedInvoiceDetail[]
}

// Interface for pending items
interface PendingItem {
  id: string
  type: 'invoice' | 'bol'
  data: {
    invoice_number?: string
    invoice_date?: string
    date?: string
    terminal?: {
      internal_name?: string
      source_name?: string
    }
    terminal_name?: string
    details?: PendingItemDetail[]
    products?: PendingItemProduct[]
    store_number?: string
    invoice_total?: number
    bol_number?: string
    bol_date?: string
    bol_total?: number
  }
}

// Interface for pending item details (for invoices)
interface PendingItemDetail {
  type?: 'supply' | 'tax'
  product?: {
    internal_name?: string
    source_name?: string
  }
  product_name?: string
  total?: number
  gross_volume?: number
  net_volume?: number
  per_unit_cost?: number
  product_total?: number
  tax_total?: number
  store_number?: string
}

// Interface for pending item products (for BOLs)
interface PendingItemProduct {
  product_name: string
  gross_volume?: number
  net_volume?: number
  product_total?: number
  tax_total?: number
}

// Interface for document rows (internal processing)
interface ProcessedDocumentRow {
  document_id: string
  document_number: string
  document_type: 'invoice' | 'bol'
  document_date: string
  timezone_data?: {
    terminal?: string
  }
  terminal?: {
    internal_name?: string
    source_name?: string
  }
  product?: {
    internal_name?: string
    source_name?: string
  }
  store_number?: string
  gross_volume?: number
  net_volume?: number
  per_unit_cost?: number
  price_id?: string
  product_total?: number
  tax_total?: number
  total?: number
  _isPending?: boolean
  _productName?: string
}

// Interface for product groups
interface ProductGroup {
  invoices: ProcessedDocumentRow[]
  bols: ProcessedDocumentRow[]
}

// Extended MatchDetailResponse to include void_invoices
interface ExtendedMatchDetailResponse extends MatchDetailResponse {
  void_invoices?: VoidedInvoice[]
  total_variance?: number
}

/**
 * Type for match summary grid rows
 */
export interface MatchSummaryRow {
  id: string
  type: 'transaction' | 'comparison' | 'total'
  source: 'invoice' | 'bol' | 'comparison' | 'total'
  product: string
  productObject?: {
    source_name: string
    internal_id?: string | null
    internal_name?: string | null
  }
  doc_type: string // Match column expectation
  number: string // Match column expectation
  date: string // Match column expectation
  terminal: string
  terminalObject?: {
    source_name: string
    internal_id?: string | null
    internal_name?: string | null
  }
  site: string
  gross_volume: number // Match column expectation
  net_volume: number // Match column expectation
  per_unit: number
  product_amount: number // Match column expectation
  tax_amount: number // Match column expectation
  total_amount: number // Match column expectation
  price_id?: string // For price breakdown functionality
  bol_number: string
  order_number: string
  _gridRowId: string
  _isPending?: boolean
}

/**
 * Helper function to normalize variance values and eliminate -0.00 display issues
 */
function normalizeVariance(value: number): number {
  // If the absolute value is very small (less than 0.005), treat as exactly 0
  // This prevents -$0.00 from appearing when there are tiny floating point errors
  return Math.abs(value) < 0.005 ? 0 : value
}

/**
 * Hook for transforming match detail data into summary grid format
 * This is the only transformation we do from API types
 */
export function useMatchSummaryData(
  matchDetails: ExtendedMatchDetailResponse | undefined,
  pendingItems: PendingItem[] = []
) {
  const matchSummaryData = useMemo<MatchSummaryRow[]>(() => {
    if (!matchDetails?.document_detail_rows) return []

    // Start with original document rows - only use document_detail_rows, not void_invoices
    let allDocumentRows = [...(matchDetails.document_detail_rows || [])]

    // Note: Voided invoices should NOT appear in the match summary grid
    // They are handled separately in the UI for display purposes only

    // Only add pending items if they exist
    if (pendingItems.length > 0) {
      const pendingDocumentRows = pendingItems
        .flatMap((pending) => {
          if (pending.type === 'invoice') {
            // For invoices, check if we have details (from API) or products (legacy)
            const details = pending.data.details || pending.data.products || []
            if (details.length > 0) {
              // Calculate total tax from all tax details for this invoice
              const totalTaxForInvoice = details
                .filter((detail: PendingItemDetail) => detail.type === 'tax')
                .reduce((sum: number, detail: PendingItemDetail) => sum + (detail.total || 0), 0)

              // Create a row for each product detail (excluding tax items)
              // But include the total tax amount distributed across products
              const supplyDetails = details.filter((detail: PendingItemDetail) => detail.type !== 'tax')
              const supplyCount = supplyDetails.length || 1
              const taxPerProduct = totalTaxForInvoice / supplyCount

              return supplyDetails.map((detail: PendingItemDetail) => ({
                document_id: pending.id,
                document_number: pending.data.invoice_number,
                document_type: 'invoice' as const,
                document_date: pending.data.invoice_date || pending.data.date,
                terminal:
                  pending.data.terminal ||
                  (pending.data.terminal_name ? { source_name: pending.data.terminal_name } : null),
                product: detail.product || (detail.product_name ? { source_name: detail.product_name } : null),
                store_number: pending.data.store_number || detail.store_number || 'Unknown Site',
                gross_volume: detail.gross_volume || 0,
                net_volume: detail.net_volume || detail.gross_volume || 0,
                per_unit_cost:
                  detail.per_unit_cost ||
                  (detail.total && detail.gross_volume ? detail.total / detail.gross_volume : 0),
                product_total: detail.product_total || detail.total || 0,
                tax_total: detail.tax_total || taxPerProduct || 0,
                total: (detail.product_total || detail.total || 0) + (detail.tax_total || taxPerProduct || 0),
                _isPending: true,
                _productName: detail.product?.internal_name || detail.product?.source_name || detail.product_name,
              }))
            } else {
              // Fallback for invoices without product details
              return [
                {
                  document_id: pending.id,
                  document_number: pending.data.invoice_number,
                  document_type: 'invoice' as const,
                  document_date: pending.data.invoice_date || pending.data.date,
                  terminal:
                    pending.data.terminal ||
                    (pending.data.terminal_name ? { source_name: pending.data.terminal_name } : null),
                  store_number: pending.data.store_number || 'Unknown Site',
                  gross_volume: pending.data.invoice_total || 0,
                  net_volume: pending.data.invoice_total || 0,
                  per_unit_cost: 0,
                  product_total: pending.data.invoice_total || 0,
                  tax_total: 0,
                  total: pending.data.invoice_total || 0,
                  _isPending: true,
                },
              ]
            }
          } else if (pending.type === 'bol') {
            // For BOL data, create a row for each product
            const products = pending.data.products || []
            if (products.length > 0) {
              return products.map((product: PendingItemProduct) => ({
                document_id: pending.id,
                document_number: pending.data.bol_number,
                document_type: 'bol' as const,
                document_date: pending.data.bol_date || pending.data.date,
                terminal: pending.data.terminal_name ? { source_name: pending.data.terminal_name } : null,
                product: product.product_name ? { source_name: product.product_name } : null,
                store_number: pending.data.store_number || 'Unknown Site',
                gross_volume: product.gross_volume || 0,
                net_volume: product.net_volume || product.gross_volume * 0.95 || 0,
                per_unit_cost: product.gross_volume > 0 ? (product.product_total || 0) / product.gross_volume : 0,
                product_total: product.product_total || 0,
                tax_total: product.tax_total || 0,
                total: (product.product_total || 0) + (product.tax_total || 0),
                _isPending: true,
                _productName: product.product_name, // Store for later use
              }))
            } else {
              // Fallback - use aggregate BOL totals when no individual products
              return [
                {
                  document_id: pending.id,
                  document_number: pending.data.bol_number,
                  document_type: 'bol' as const,
                  document_date: pending.data.bol_date || pending.data.date,
                  terminal:
                    pending.data.terminal ||
                    (pending.data.terminal_name ? { source_name: pending.data.terminal_name } : null),
                  store_number: pending.data.store_number || 'Unknown Site',
                  gross_volume: 0,
                  net_volume: 0,
                  per_unit_cost: 0,
                  product_total: pending.data.product_total || 0,
                  tax_total: pending.data.tax_total || 0,
                  total: pending.data.total || pending.data.bol_total || 0,
                  _isPending: true,
                },
              ]
            }
          }
          return []
        })
        .filter(Boolean)

      // Add pending rows to the existing ones
      allDocumentRows = [...allDocumentRows, ...pendingDocumentRows]
    }

    // Group document rows by product - support multiple invoices/BOLs per product
    const productGroups = new Map<string, ProductGroup>()

    // Since we're not using tax transactions anymore, we'll create a single group
    // This simplifies the logic and relies on the tax_total already in document rows
    const productNames = new Set<string>()

    // Process each document row (including pending) and assign to product groups
    allDocumentRows.forEach((row, index) => {
      // Determine product for grouping - use internal_name with fallback to source_name
      let groupingProduct = 'N/A' // Default when no product

      // For pending items that have product data, we can still use that for grouping
      if (row._isPending && row._productName) {
        groupingProduct = row._productName
      } else if (row.product) {
        // Use internal_name if available, otherwise fall back to source_name
        groupingProduct = row.product.internal_name || row.product.source_name || 'N/A'
      }

      // Add to product names set
      productNames.add(groupingProduct)

      // Initialize product group if needed
      if (!productGroups.has(groupingProduct)) {
        productGroups.set(groupingProduct, { invoices: [], bols: [] })
      }

      const group = productGroups.get(groupingProduct)!
      if (row.document_type === 'invoice') {
        group.invoices.push(row)
      } else {
        group.bols.push(row)
      }
    })

    // Build the final data with proper row structure
    const finalData: MatchSummaryRow[] = []
    let rowIndex = 0

    productGroups.forEach((group, groupingProduct) => {
      const hasInvoices = group.invoices.length > 0
      const hasBols = group.bols.length > 0

      // Add all invoice rows for this product
      group.invoices.forEach((invoice, idx) => {
        finalData.push({
          ...invoice,
          id: invoice.document_number,
          type: 'transaction',
          source: 'invoice',
          product: invoice.product?.internal_name || invoice.product?.source_name || 'N/A',
          productObject: invoice.product,
          doc_type: 'INV',
          number: invoice.document_number,
          // Use document_date from individual rows to show actual document dates
          date: formatInvoiceDate(invoice.document_date, invoice?.timezone_data?.terminal),
          terminal: invoice.terminal?.internal_name || invoice.terminal?.source_name || 'Unknown Terminal',
          terminalObject: invoice.terminal,
          site: invoice.store_number || 'Unknown Site',
          gross_volume: invoice.gross_volume || 0,
          net_volume: invoice.net_volume || 0,
          billing_method: invoice.billing_method,
          per_unit: invoice.per_unit_cost || 0,
          product_amount: invoice.product_total || 0,
          tax_amount: invoice.tax_total || 0,
          total_amount: invoice.total || 0,
          _gridRowId: `invoice_${groupingProduct}_${idx}_${rowIndex++}`,
          _isPending: invoice._isPending || false,
        })
      })

      // Add all BOL rows for this product
      group.bols.forEach((bol, idx) => {
        finalData.push({
          ...bol,
          id: bol.document_number,
          type: 'transaction',
          source: 'bol',
          product: bol.product?.internal_name || bol.product?.source_name || 'N/A',
          productObject: bol.product,
          doc_type: 'BOL',
          number: bol.document_number,
          // Use document_date from individual rows to show actual document dates
          date: formatters.DATE_TIME(bol.document_date, bol?.timezone_data?.terminal) ?? 'N/A',
          terminal: bol.terminal?.internal_name || bol.terminal?.source_name || 'Unknown Terminal',
          terminalObject: bol.terminal,
          site: bol.store_number || 'Unknown Site',
          gross_volume: bol.gross_volume || 0,
          net_volume: bol.net_volume || 0,
          billing_method: bol.billing_method,
          per_unit: bol.per_unit_cost || 0,
          product_amount: bol.product_total || 0,
          tax_amount: bol.tax_total || 0,
          total_amount: bol.total || 0,
          price_id: bol.price_id, // Extract price_id for BOL rows
          _gridRowId: `bol_${groupingProduct}_${idx}_${rowIndex++}`,
          _isPending: bol._isPending || false,
        })
      })

      // Add comparison row if we have both invoices and BOLs
      // Calculate totals for all invoices and all BOLs
      if (hasInvoices && hasBols) {
        const invoiceTotals = group.invoices.reduce(
          (acc, inv) => ({
            gross_volume: acc.gross_volume + (inv.gross_volume || 0),
            net_volume: acc.net_volume + (inv.net_volume || 0),
            product_amount: acc.product_amount + (inv.product_total || 0),
            tax_amount: acc.tax_amount + (inv.tax_total || 0),
            total_amount: acc.total_amount + (inv.total || 0),
            per_unit_sum: acc.per_unit_sum + (inv.per_unit_cost || 0),
            count: acc.count + 1,
          }),
          {
            gross_volume: 0,
            net_volume: 0,
            product_amount: 0,
            tax_amount: 0,
            total_amount: 0,
            per_unit_sum: 0,
            count: 0,
          }
        )

        const bolTotals = group.bols.reduce(
          (acc, bol) => ({
            gross_volume: acc.gross_volume + (bol.gross_volume || 0),
            net_volume: acc.net_volume + (bol.net_volume || 0),
            product_amount: acc.product_amount + (bol.product_total || 0),
            tax_amount: acc.tax_amount + (bol.tax_total || 0),
            total_amount: acc.total_amount + (bol.total || 0),
            per_unit_sum: acc.per_unit_sum + (bol.per_unit_cost || 0),
            count: acc.count + 1,
          }),
          {
            gross_volume: 0,
            net_volume: 0,
            product_amount: 0,
            tax_amount: 0,
            total_amount: 0,
            per_unit_sum: 0,
            count: 0,
          }
        )

        // Calculate per unit variance by summing individual per unit costs (like TOTAL row)
        const invoicePerUnitSum = group.invoices.reduce((sum, inv) => sum + (inv.per_unit_cost || 0), 0)
        const bolPerUnitSum = group.bols.reduce((sum, bol) => sum + (bol.per_unit_cost || 0), 0)
        const perUnitVariance = invoicePerUnitSum - bolPerUnitSum

        finalData.push({
          id: 'Variance',
          type: 'comparison',
          source: 'comparison',
          product: groupingProduct,
          doc_type: '',
          number: '',
          date: '',
          terminal: '',
          site: '',
          gross_volume: normalizeVariance(invoiceTotals.gross_volume - bolTotals.gross_volume),
          net_volume: normalizeVariance(invoiceTotals.net_volume - bolTotals.net_volume),
          per_unit: normalizeVariance(perUnitVariance || 0),
          product_amount: normalizeVariance(invoiceTotals.product_amount - bolTotals.product_amount),
          tax_amount: normalizeVariance(invoiceTotals.tax_amount - bolTotals.tax_amount),
          total_amount: normalizeVariance(invoiceTotals.total_amount - bolTotals.total_amount),
          _gridRowId: `comparison_${groupingProduct}_${rowIndex++}`,
        })
      }
    })

    // Add a total row if there are multiple products
    if (productGroups.size > 1) {
      // Calculate separate totals for invoices and BOLs
      const invoiceTotals = finalData
        .filter((row) => row.type === 'transaction' && row.source === 'invoice')
        .reduce(
          (acc, row) => ({
            gross_volume: acc.gross_volume + row.gross_volume,
            net_volume: acc.net_volume + row.net_volume,
            product_amount: acc.product_amount + row.product_amount,
            tax_amount: acc.tax_amount + row.tax_amount,
            total_amount: acc.total_amount + row.total_amount,
            per_unit_sum: acc.per_unit_sum + row.per_unit,
          }),
          { gross_volume: 0, net_volume: 0, product_amount: 0, tax_amount: 0, total_amount: 0, per_unit_sum: 0 }
        )

      const bolTotals = finalData
        .filter((row) => row.type === 'transaction' && row.source === 'bol')
        .reduce(
          (acc, row) => ({
            gross_volume: acc.gross_volume + row.gross_volume,
            net_volume: acc.net_volume + row.net_volume,
            product_amount: acc.product_amount + row.product_amount,
            tax_amount: acc.tax_amount + row.tax_amount,
            total_amount: acc.total_amount + row.total_amount,
            per_unit_sum: acc.per_unit_sum + row.per_unit,
          }),
          { gross_volume: 0, net_volume: 0, product_amount: 0, tax_amount: 0, total_amount: 0, per_unit_sum: 0 }
        )

      // Calculate total variances (invoice totals - BOL totals)
      // When pending items exist, always calculate from current data; otherwise use API values
      const hasPendingItems = pendingItems && pendingItems.length > 0
      const totalGrossVolumeVariance = hasPendingItems
        ? invoiceTotals.gross_volume - bolTotals.gross_volume
        : (matchDetails?.gross_volume_variance ?? invoiceTotals.gross_volume - bolTotals.gross_volume)
      const totalNetVolumeVariance = hasPendingItems
        ? invoiceTotals.net_volume - bolTotals.net_volume
        : (matchDetails?.net_volume_variance ?? invoiceTotals.net_volume - bolTotals.net_volume)
      const totalProductVariance = hasPendingItems
        ? invoiceTotals.product_amount - bolTotals.product_amount
        : (matchDetails?.product_total_variance ?? invoiceTotals.product_amount - bolTotals.product_amount)
      const totalTaxVariance = hasPendingItems
        ? invoiceTotals.tax_amount - bolTotals.tax_amount
        : (matchDetails?.tax_total_variance ?? invoiceTotals.tax_amount - bolTotals.tax_amount)
      const totalVariance = hasPendingItems
        ? invoiceTotals.total_amount - bolTotals.total_amount
        : (matchDetails?.total_variance ?? invoiceTotals.total_amount - bolTotals.total_amount)

      // Calculate per unit variance: sum(invoice per units) - sum(BOL per units)
      // This matches exactly how individual variance rows calculate it
      const totalPerUnitVariance = hasPendingItems
        ? invoiceTotals.per_unit_sum - bolTotals.per_unit_sum
        : (matchDetails?.per_unit_variance ?? 0)

      finalData.push({
        id: 'Total',
        type: 'total',
        source: 'total',
        product: 'VARIANCE',
        doc_type: '',
        number: '',
        date: '',
        terminal: '',
        site: '',
        gross_volume: normalizeVariance(totalGrossVolumeVariance),
        net_volume: normalizeVariance(totalNetVolumeVariance),
        per_unit: normalizeVariance(totalPerUnitVariance),
        product_amount: normalizeVariance(totalProductVariance),
        tax_amount: normalizeVariance(totalTaxVariance),
        total_amount: normalizeVariance(totalVariance),
        _gridRowId: `total_${rowIndex++}`,
      })
    }

    return finalData
  }, [matchDetails, pendingItems])

  // Calculate grid height based on rows
  const getMatchRowHeight = (row: MatchSummaryRow) => {
    if (!row) return 30
    // Total row gets 40px height
    if (row.type === 'total') return 40
    // All other rows get 30px (restored from old version)
    return 30
  }

  const getMatchGridHeight = (rows: MatchSummaryRow[]) => {
    let totalHeight = 0
    for (const row of rows) {
      totalHeight += getMatchRowHeight(row)
    }
    // Add UI overhead: 48px header + 16px padding (increased for better spacing)
    return totalHeight + 56
  }

  // Limit the height to a reasonable maximum
  const calculatedHeight = getMatchGridHeight(matchSummaryData)
  const matchSummaryGridHeight = Math.min(Math.max(calculatedHeight, 150), 400)

  // Calculate variance: use original when no pending items, calculate from data when pending items exist
  const totalVarianceWithPending = useMemo(() => {
    const originalVariance = matchDetails?.total_variance || 0

    // If no pending items, return original variance (don't recalculate)
    if (!pendingItems || pendingItems.length === 0) {
      return originalVariance
    }

    // If no summary data, return original variance
    if (!matchSummaryData || matchSummaryData.length === 0) {
      return originalVariance
    }

    // Calculate total variance by comparing invoice totals vs BOL totals
    const invoiceRows = matchSummaryData.filter((row) => row.type === 'transaction' && row.source === 'invoice')
    const bolRows = matchSummaryData.filter((row) => row.type === 'transaction' && row.source === 'bol')

    const invoiceTotals = invoiceRows.reduce((total, row) => total + row.total_amount, 0)
    const bolTotals = bolRows.reduce((total, row) => total + row.total_amount, 0)

    // Variance is invoice total minus BOL total
    return invoiceTotals - bolTotals
  }, [matchSummaryData, pendingItems, matchDetails?.total_variance])

  return {
    matchSummaryData,
    getMatchRowHeight,
    matchSummaryGridHeight,
    totalVarianceWithPending,
  }
}
